import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface CoachNote {
  id: string;
  coach_id: string;
  leader_id: string;
  content: string;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

export type CoachNoteWriteResult =
  | { readonly status: "saved"; readonly note: CoachNote }
  | { readonly status: "saved_readback_failed"; readonly note: CoachNote; readonly readbackError: Error };

interface UseCoachNotesOptions {
  readonly enabled?: boolean;
  readonly accessGeneration?: string;
}

const RELATIONSHIP_AUTHORIZATION_ERROR = "Relationship authorization required";

export function useCoachNotes(
  leaderId: string,
  { enabled = true, accessGeneration = "legacy" }: UseCoachNotesOptions = {},
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canQuery = Boolean(enabled && user?.id && leaderId);

  const queryKey = ["coach-notes", user?.id, leaderId, accessGeneration] as const;
  const loadNotes = async () => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("coach_notes")
        .select("*")
        .eq("leader_id", leaderId)
        .eq("coach_id", user.id)
        .order("pinned", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as CoachNote[];
  };
  const readBackNotes = async (guard: (notes: CoachNote[]) => boolean) => {
    await queryClient.invalidateQueries({ queryKey, refetchType: "none" });
    const readback = await queryClient.fetchQuery({ queryKey, queryFn: loadNotes });
    if (!guard(readback)) throw new Error("The saved note could not be read back.");
  };

  const notesQuery = useQuery({
    queryKey,
    queryFn: loadNotes,
    enabled: canQuery,
  });

  const addNoteMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!enabled) throw new Error(RELATIONSHIP_AUTHORIZATION_ERROR);
      if (!user) throw new Error("Not authenticated");
      
      const { data, error } = await supabase
        .from("coach_notes")
        .insert({
          coach_id: user.id,
          leader_id: leaderId,
          content: content.trim(),
        })
        .select()
        .single();

      if (error) throw error;
      const saved = data as CoachNote;
      try {
        await readBackNotes((notes) => notes.some((note) => note.id === saved.id));
        return { status: "saved", note: saved } satisfies CoachNoteWriteResult;
      } catch (readbackError) {
        return {
          status: "saved_readback_failed",
          note: saved,
          readbackError: readbackError instanceof Error ? readbackError : new Error("Note readback failed"),
        } satisfies CoachNoteWriteResult;
      }
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({ id, content, pinned }: { id: string; content?: string; pinned?: boolean }) => {
      if (!enabled) throw new Error(RELATIONSHIP_AUTHORIZATION_ERROR);
      if (!user) throw new Error("Not authenticated");
      const updates: Partial<CoachNote> = {};
      if (content !== undefined) updates.content = content.trim();
      if (pinned !== undefined) updates.pinned = pinned;

      const { data, error } = await supabase
        .from("coach_notes")
        .update(updates)
        .eq("id", id)
        .eq("leader_id", leaderId)
        .eq("coach_id", user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (saved: CoachNote) => {
      await readBackNotes((notes) => notes.some((note) => (
        note.id === saved.id
        && note.content === saved.content
        && note.pinned === saved.pinned
        && note.updated_at === saved.updated_at
      )));
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!enabled) throw new Error(RELATIONSHIP_AUTHORIZATION_ERROR);
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("coach_notes")
        .delete()
        .eq("id", id)
        .eq("leader_id", leaderId)
        .eq("coach_id", user.id);

      if (error) throw error;
    },
    onSuccess: async (_data, deletedId) => {
      await readBackNotes((notes) => notes.every((note) => note.id !== deletedId));
    },
  });

  return {
    notes: enabled ? notesQuery.data || [] : [],
    isLoading: enabled ? notesQuery.isLoading : false,
    isRefetching: enabled ? notesQuery.isRefetching : false,
    error: enabled ? notesQuery.error : null,
    refetchNotes: notesQuery.refetch,
    addNote: addNoteMutation.mutateAsync,
    isAdding: addNoteMutation.isPending,
    updateNote: updateNoteMutation.mutateAsync,
    isUpdating: updateNoteMutation.isPending,
    deleteNote: deleteNoteMutation.mutateAsync,
    isDeleting: deleteNoteMutation.isPending,
  };
}
